import React, { useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';

import { IconButton } from '@components/button/Button';
import { Event, sendMixpanelEvent } from '@core/analytics/mixpanel';
import useNotify from '@hooks/notify';
import ModalV2 from '@layouts/modal/Modal.v2';

import VoiceNotePlayer from './VoiceNotePlayer';

import { UploadStatus } from '../../constants/leadFileUpload';
import { useLeadFileUpload } from '../../hooks/useLeadFileUpload';
import { useCreateLeadVoiceNoteMutation } from '../../queries/LeadManagementQuery';
import {
  downsampleLevels,
  getDefaultVoiceNoteName,
  getVoiceNoteFileName
} from '../../utils/voiceNote';

const MAX_NAME_LENGTH = 100;
const PREVIEW_BARS = 56;

export type VoiceRecording = {
  file: File;
  durationSec: number;
  levels: number[];
};

interface SaveVoiceNoteModalProps {
  organizationId: string;
  leadId: string;
  recording: VoiceRecording;
  onClose: () => void;
  onSaved: () => void;
}

// Shown after a recording finishes: rename (defaults to "Voice<HHmmss>"),
// preview, then Save or Discard. Upload starts as soon as the popup opens.
const SaveVoiceNoteModal: React.FC<SaveVoiceNoteModalProps> = ({
  organizationId,
  leadId,
  recording,
  onClose,
  onSaved
}) => {
  const { currentOrg } = useSelector((state: any) => state.user);
  const { createSuccessAlert, createErrorAlert } = useNotify();
  const [defaultName] = useState(getDefaultVoiceNoteName);
  const [name, setName] = useState(defaultName);
  const [isEditingName, setIsEditingName] = useState(false);
  const [url] = useState(() => URL.createObjectURL(recording.file));
  const [bars] = useState(() =>
    downsampleLevels(recording.levels, PREVIEW_BARS)
  );
  const upload = useLeadFileUpload({
    organizationId,
    onError: createErrorAlert
  });
  const { addFiles, reset } = upload;
  const [createVoiceNote, { isLoading: isSaving }] =
    useCreateLeadVoiceNoteMutation();

  const uploaded = upload.items[0];
  const isUploading = uploaded?.status === UploadStatus.IN_PROGRESS;

  // StrictMode re-runs effects in development; upload once.
  const hasStartedUploadRef = useRef(false);
  useEffect(() => {
    if (hasStartedUploadRef.current) return;
    hasStartedUploadRef.current = true;
    addFiles([recording.file]);
  }, [addFiles, recording.file]);

  useEffect(() => () => URL.revokeObjectURL(url), [url]);

  const retryUpload = () => {
    reset();
    addFiles([recording.file]);
  };

  const handleSave = async () => {
    if (!uploaded?.objectKey) return;
    const contentType = recording.file.type;
    try {
      const saved = await createVoiceNote({
        organizationId,
        leadId,
        body: {
          file_name: getVoiceNoteFileName(contentType, name || defaultName),
          object_key: uploaded.objectKey,
          file_size: String(recording.file.size),
          content_type: contentType
        }
      }).unwrap();
      sendMixpanelEvent(Event.LEAD_VOICE_NOTE_CREATED, {
        org_id: organizationId,
        org_name: currentOrg?.name,
        lead_id: leadId,
        voice_note_id: saved?.id,
        duration_sec: recording.durationSec
      });
      createSuccessAlert('Voice note saved successfully');
      onSaved();
      onClose();
    } catch {
      createErrorAlert('Failed to save voice note');
    }
  };

  const title = isEditingName ? (
    <input
      // Focus moves here right after the pencil is clicked.
      // eslint-disable-next-line jsx-a11y/no-autofocus
      autoFocus
      value={name}
      maxLength={MAX_NAME_LENGTH}
      onChange={(event) => setName(event.target.value)}
      onBlur={() => {
        setName((prev) => prev.trim() || defaultName);
        setIsEditingName(false);
      }}
      onKeyDown={(event) => {
        if (event.key === 'Enter') event.currentTarget.blur();
      }}
      aria-label="Voice note name"
      className="w-full rounded-md border border-grey30 px-2 py-1 text-lg font-semibold focus:border-cofeePrimary6 focus:outline-none"
    />
  ) : (
    <span className="flex items-center gap-2">
      <span className="truncate">{name}</span>
      <IconButton
        iconName="edit-grey"
        variant="transparent"
        size="none"
        iconSize="medium"
        className="!p-0"
        handleClick={() => setIsEditingName(true)}
        aria-label="Rename voice note"
      />
    </span>
  );

  return (
    <ModalV2
      title={title}
      onClose={onClose}
      className="w-[560px] !rounded-2xl"
      titleClassName="min-w-0 flex-1"
      primaryButtonLabel="Save"
      primaryButtonClick={handleSave}
      primaryButtonDisabled={!uploaded?.objectKey}
      primaryButtonLoading={isSaving || isUploading}
      secondaryButtonLabel="Discard"
      secondaryButtonClick={onClose}
    >
      <div className="flex flex-col gap-2">
        <VoiceNotePlayer
          src={url}
          durationSec={recording.durationSec}
          bars={bars}
        />
        {uploaded?.status === UploadStatus.FAILED && (
          <p className="text-sm text-red-500">
            Upload failed.{' '}
            <button
              type="button"
              onClick={retryUpload}
              className="font-medium underline"
            >
              Try again
            </button>
          </p>
        )}
      </div>
    </ModalV2>
  );
};

export default SaveVoiceNoteModal;
